// packages/ui/src/forms/fields/CountrySelectorInput.tsx
'use client';

import { useMemo, type ReactElement, type ReactNode } from 'react';
import type { FieldValues, Path } from 'react-hook-form';
import { Box, Typography, type TextFieldProps } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';

import { Flag } from '@tourtask/ui/components/Flag';
import { FlagWithLabel } from '@tourtask/ui/components/FlagWithLabel';
import { FormAutocomplete, type FormAutocompleteComponent } from './FormAutocomplete';

import type { CountryWithName } from '@tourtask/types/configs/Country';

export type CountrySelectorInputProps<TFieldValues extends FieldValues> = Props<TFieldValues>;

/** CountrySelectorInput's own type, which `usePhoneInput` takes (MUI's, or the chat widget's outlined one). */
export type CountrySelectorInputComponent = <TFieldValues extends FieldValues>(
  props: CountrySelectorInputProps<TFieldValues>,
) => ReactElement;

type Props<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  countriesWithNames: CountryWithName[];
  label: string;
  disabled?: boolean;
  required?: boolean;
  leftIcon?: ReactNode;
  highlightWhenEmpty?: boolean;
  /** See `getFieldFillSx`. Defaults to `'white'`. */
  background?: 'white' | 'transparent';
  helperText?: string;
  textFieldProps?: Omit<TextFieldProps, 'label' | 'error' | 'helperText'>;
  /**
   * When true, dropdown options render the country's calling code as
   * gray secondary text next to the name (e.g. "Chile +56"), and typing
   * digits filters by calling code in addition to the name. Used by
   * `PhoneInput` so users can find a country by dialling code.
   */
  showCallingCode?: boolean;
  /**
   * Compact dropdown for narrow containers (set by `PhoneInput`'s `compact`):
   * each option shows just the flag + a truncating name and DROPS the per-option
   * calling code, which otherwise overlaps the name when the dropdown is narrow
   * (the dropdown is sized to the input width). Filtering by dial code still works
   * when `showCallingCode` is also set.
   */
  compact?: boolean;
};

export function CountrySelectorInput<TFieldValues extends FieldValues>(props: Props<TFieldValues>) {
  return useCountrySelectorInput(FormAutocomplete, props);
}

/**
 * CountrySelectorInput, drawn with `FormAutocompleteComponent` (see `useFormTextField`: the chat widget's pre-chat
 * form draws it with an outlined-only one).
 */
export function useCountrySelectorInput<TFieldValues extends FieldValues>(
  FormAutocompleteComponent: FormAutocompleteComponent,
  {
    name,
    countriesWithNames,
    label,
    disabled,
    required,
    leftIcon,
    highlightWhenEmpty = false,
    background = 'white',
    helperText,
    textFieldProps,
    showCallingCode = false,
    compact = false,
  }: Props<TFieldValues>,
): ReactElement {
  const filterOptions = useMemo(() => {
    if (!showCallingCode) return undefined;
    return createFilterOptions<CountryWithName>({
      stringify: (c) => `${c.name} +${c.callingCode} ${c.callingCode}`,
    });
  }, [showCallingCode]);

  // Width of the longest "+CCCC" across all options, in `ch` units. Used
  // to right-align the calling-code column without reserving more space
  // than the longest code actually needs.
  const callingCodeColumnMinWidth = useMemo(() => {
    if (!showCallingCode) return undefined;
    const maxChars = countriesWithNames.reduce(
      (m, c) => Math.max(m, String(c.callingCode).length + 1),
      0,
    );
    return maxChars > 0 ? `${maxChars}ch` : undefined;
  }, [countriesWithNames, showCallingCode]);

  return (
    <FormAutocompleteComponent<TFieldValues, CountryWithName>
      name={name}
      options={countriesWithNames}
      label={label}
      disabled={disabled}
      required={required}
      helperText={helperText}
      leftIcon={leftIcon}
      highlightWhenEmpty={highlightWhenEmpty}
      background={background}
      // Country selectors hide the clear (X) button: clearing a country back to
      // "" is not a useful end state anywhere in the repo — users re-select
      // instead. A `clearable` opt-in briefly existed for the manual-payment
      // payee account, whose `countryCode` was nullable; the column is NOT NULL
      // since 2026-08-26_1050 and the prop went with it rather than surviving as
      // a path nothing takes.
      disableClearable
      textFieldProps={textFieldProps}
      selectSingleFilteredOptionOnEnter
      filterOptions={filterOptions}
      getSelectedAdornment={(country) =>
        country ? (
          <Flag countryCode={country.code} style={{ width: '1.15em', height: '1.15em' }} />
        ) : null
      }
      getOptionLabel={(country) => country.name}
      getOptionValue={(country) => country.code}
      renderOption={(country) =>
        compact ? (
          // Narrow dropdown: flag + a TRUNCATING name, no calling code (it has no
          // room beside the name here, and it's hidden in the field too). Rendered
          // inline rather than via FlagWithLabel so the name can actually ellipsize
          // (FlagWithLabel's inline-flex label has no width constraint).
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
            <Flag countryCode={country.code} style={{ width: '1.15em', height: '1.15em', flexShrink: 0 }} />
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {country.name}
            </Typography>
          </Box>
        ) : showCallingCode ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FlagWithLabel countryCode={country.code} label={country.name} />
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                flex: '0 0 auto',
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
                minWidth: callingCodeColumnMinWidth,
              }}
            >
              +{country.callingCode}
            </Typography>
          </Box>
        ) : (
          <FlagWithLabel countryCode={country.code} label={country.name} />
        )
      }
    />
  );
}
